import { Modal as MuiModal, Stack, styled } from '@mui/material'
import { ModalHeader } from './headers/ModalHeader'
import { ModalFooter, ModalFooterProps } from './footers/ModalFooter'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { forwardRef, ReactNode } from 'react'

type ModalProps = {
    title: ReactNode
    onClose: () => void
    isOpen: boolean
    showCloseIcon?: boolean
    showLoading?: boolean
    showHeaderLoading?: boolean
    closeIconTooltip?: string
    disableBackdropClose?: boolean
    children: ReactNode
    footerProps?: ModalFooterProps
    footer?: ReactNode
    fullWidth?: boolean
    fullHeight?: boolean
    width?: `${number}px` | 'fit-content'
    height?: `${number}px`
    removeContentPadding?: boolean
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(function Modal(
    {
        title,
        onClose,
        isOpen,
        showCloseIcon,
        children,
        footerProps,
        footer,
        width,
        showLoading,
        showHeaderLoading,
        closeIconTooltip,
        fullWidth = false,
        fullHeight = false,
        disableBackdropClose = false,
        height,
        removeContentPadding = false,
    },
    ref
) {
    return (
        <ModalWrapper open={isOpen} onClose={disableBackdropClose ? () => undefined : onClose} ref={ref}>
            <ModalContent
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === 'string' ? title : undefined}
                $width={width}
                $height={height}
                $fullWidth={fullWidth}
                $fullHeight={fullHeight}
            >
                <ModalHeader
                    title={title}
                    handleCloseModal={onClose}
                    showCloseIcon={showCloseIcon}
                    showLoading={showHeaderLoading}
                    closeIconTooltip={closeIconTooltip}
                />
                <ModalContentShellContainer $removePadding={removeContentPadding}>{children}</ModalContentShellContainer>
                {footer ?? (footerProps && <ModalFooter {...footerProps} fullWidth={fullWidth} isPending={showLoading} />)}
            </ModalContent>
        </ModalWrapper>
    )
})

const ModalWrapper = styled(MuiModal)`
    display: flex;
    justify-content: center;
    align-items: center;
`

const ModalContent = styled(Stack, { shouldForwardProp: (prop: string) => prop[0] !== '$' })(
    ({
        $fullWidth,
        $fullHeight,
        $width,
        $height,
    }: {
        $fullWidth: boolean
        $fullHeight: boolean
        $width?: `${number}px` | 'fit-content'
        $height?: `${number}px`
    }) => ({
        width: $width || ($fullWidth ? '1208px' : '580px'),
        height: $height || ($fullHeight ? 'calc(100% - 48px)' : undefined),
        maxHeight: 'calc(100% - 48px)',
        backgroundColor: tokens.ColorBackgroundLight,
        padding: tokens.SizingSpacingNone,
        '&:focus': {
            outline: 'none',
        },
    })
)

const ModalContentShellContainer = styled(Stack, { shouldForwardProp: (prop: string) => prop[0] !== '$' })<{
    $removePadding: boolean
}>(({ $removePadding }) => ({
    width: '100%',
    flexGrow: 1,
    padding: $removePadding ? tokens.SizingSpacingNone : tokens.SizingSpacing300,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: tokens.SizingSpacing600,
    background: tokens.ColorSurfaceDefault,
    overflow: 'auto',
    maxHeight: 'calc(100% - 48px)',
}))
